// Today-on-the-map dispatcher view. Pins for every job scheduled today/tomorrow,
// colored by status, popup shows tech + customer + ETA.
import React, { useEffect, useMemo, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Spinner, Select, Btn } from '../ui.jsx';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
async function loadLeaflet() {
  if (window.L) return window.L;
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement('link'); link.rel='stylesheet'; link.href=LEAFLET_CSS; document.head.appendChild(link);
  }
  await new Promise((res, rej) => {
    if (document.querySelector(`script[src="${LEAFLET_JS}"]`)) return res();
    const s = document.createElement('script'); s.src = LEAFLET_JS; s.onload = res; s.onerror = rej; document.body.appendChild(s);
  });
  return window.L;
}

const STATUS_COLOR = { Scheduled:'#3b82f6', Booked:'#22d3ee', Dispatched:'#a78bfa', 'En Route':'#22d3ee', 'On Site':'#f97316', Completed:'#22c55e', Cancelled:'#ef4444' };

export function JobsMap({ toast }) {
  const [jobs, setJobs] = useState(null);
  const [day, setDay] = useState('today');
  const [techs, setTechs] = useState([]);

  useEffect(() => { api('/api/jobs').then(setJobs).catch(e=>toast(e.message,'error')); }, []);
  // Live tech locations — re-poll every 30s
  useEffect(() => {
    const load = () => api('/api/techs/live').then(setTechs).catch(()=>{});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    if (!jobs) return [];
    const today = new Date().toISOString().slice(0,10);
    const tomorrow = new Date(Date.now()+86400000).toISOString().slice(0,10);
    const target = day === 'today' ? today : day === 'tomorrow' ? tomorrow : null;
    return jobs.filter(j => {
      if (!target) return true;
      return (j.scheduledDate||'').slice(0,10) === target;
    }).filter(j => j.status !== 'Cancelled');
  }, [jobs, day]);

  useEffect(() => {
    let L, leafletMap, layer;
    let cancelled = false;
    (async () => {
      try {
        L = await loadLeaflet(); if (cancelled) return;
        if (!document.getElementById('jobs-map-root')) return;
        leafletMap = L.map('jobs-map-root').setView([27.9506, -82.4572], 10);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution:'© OpenStreetMap', maxZoom:19 }).addTo(leafletMap);
        layer = L.layerGroup().addTo(leafletMap);
        const bounds = [];
        for (const j of filtered) {
          const addr = j.address || (j.contact ? [j.contact.address, j.contact.city, j.contact.state, j.contact.zip].filter(Boolean).join(', ') : '');
          if (!addr) continue;
          let r = null;
          try { r = await api(`/api/contacts/geocode?address=${encodeURIComponent(addr)}`); } catch {}
          if (!r?.lat || !r?.lng || cancelled) continue;
          const color = STATUS_COLOR[j.status] || '#a78bfa';
          const pin = L.circleMarker([r.lat, r.lng], { radius:10, color, fillColor:color, fillOpacity:.85, weight:2 }).addTo(layer);
          pin.bindPopup(`
            <div style="font-family:system-ui,sans-serif;min-width:200px">
              <div style="font-weight:700">${j.name||'Job'}</div>
              <div style="color:#888;font-size:12px">${j.status}${j.startTime?` · ${j.startTime}`:''}${j.techName?` · ${j.techName}`:''}</div>
              ${j.contact ? `<div style="margin-top:6px">${j.contact.firstName||''} ${j.contact.lastName||''} · <a href="tel:${j.contact.phone}" style="color:#22d3ee">${j.contact.phone||''}</a></div>` : ''}
              <div style="font-size:11px;color:#888;margin-top:4px">${addr}</div>
              <div style="margin-top:8px">
                <a href="https://maps.apple.com/?q=${encodeURIComponent(addr)}" target="_blank" style="color:#a78bfa;font-size:12px;font-weight:600">Open in Maps →</a>
              </div>
            </div>`);
          bounds.push([r.lat, r.lng]);
        }

        // Tech pins — square marker so dispatchers can distinguish them from jobs.
        // Color is the tech's assigned color when fresh, grey when stale signal.
        for (const t of techs) {
          if (typeof t.lat !== 'number' || typeof t.lng !== 'number') continue;
          const color = t.stale ? '#9ca3af' : (t.color || '#22c55e');
          const icon = L.divIcon({
            html: `<div style="width:26px;height:26px;border-radius:6px;background:${color};border:3px solid #fff;box-shadow:0 0 0 2px ${color};display:flex;align-items:center;justify-content:center;font-size:14px">🚐</div>`,
            iconSize:[26,26], className:'',
          });
          const m = L.marker([t.lat, t.lng], { icon }).addTo(layer);
          const ageMin = t.lastSeenAt ? Math.round((Date.now() - new Date(t.lastSeenAt).getTime())/60000) : null;
          m.bindPopup(`
            <div style="font-family:system-ui,sans-serif;min-width:180px">
              <div style="font-weight:700">🚐 ${t.name}</div>
              <div style="color:#888;font-size:12px">${t.stale ? 'Stale signal' : 'Live'} ${ageMin!=null?`· ${ageMin}m ago`:''}</div>
              ${t.job ? `<div style="margin-top:6px;font-size:12px">On job: <strong>${t.job.name||''}</strong></div>` : ''}
            </div>`);
          bounds.push([t.lat, t.lng]);
        }

        if (bounds.length) leafletMap.fitBounds(bounds, { padding:[30,30] });
      } catch (e) { console.error('Map failed:', e); }
    })();
    return () => { cancelled = true; if (leafletMap) leafletMap.remove(); };
  }, [filtered.map(j => j.id).join(','), techs.map(t => `${t.id}:${t.lat},${t.lng}`).join(',')]);

  if (!jobs) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;

  return <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14, height:'calc(100vh - 64px)' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14 }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Today on the Map</h1>
        <div style={{ color:G.textMuted, fontSize:13, marginTop:4 }}>Dispatcher view — see where everyone is + drive route efficiency.</div>
      </div>
      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        <Select value={day} onChange={e=>setDay(e.target.value)} style={{ maxWidth:160 }}>
          <option value="today">📅 Today</option>
          <option value="tomorrow">→ Tomorrow</option>
          <option value="all">All upcoming</option>
        </Select>
        <span style={{ fontSize:12, color:G.textMuted }}>{filtered.length} job{filtered.length===1?'':'s'}</span>
      </div>
    </div>

    <div style={{ display:'flex', gap:10, flexWrap:'wrap', fontSize:11 }}>
      {Object.entries(STATUS_COLOR).map(([status, color]) => (
        <span key={status} style={{ display:'flex', alignItems:'center', gap:5 }}>
          <span style={{ width:10, height:10, borderRadius:5, background:color, display:'inline-block' }}/>{status}
        </span>
      ))}
    </div>

    <div id="jobs-map-root" style={{ flex:1, borderRadius:14, overflow:'hidden', border:`1px solid ${G.cardBorder}` }}/>
  </div>;
}
